import debounce from 'lodash.debounce';
import { proxy, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

import defaultSkinTone from '@cartwhl/3d/gltf/defaultSkinTone';
import postScene from '@cartwhl/common/api/postScene';
import {
  BackgroundSettingsV1,
  CameraSettingsV1,
  CharacterSettingsV1,
  FACE_EXPRESSIONS,
  HAND_OPTIONS,
  LabelSettingsV1,
  PlaybackSettingsV1,
  SceneSettings,
} from '@cartwhl/common/types';
import { setStoreValues } from '@cartwhl/common/utils';

import {
  animationGenerationStore,
  authStore,
  characterPickerStore,
  projectStore,
} from 'stores';

type SceneSettingsState = {
  backgroundSettings: Omit<BackgroundSettingsV1, 'version'>;
  cameraSettings: Omit<CameraSettingsV1, 'version'>;
  characterSettings: Omit<CharacterSettingsV1, 'version'>;
  labelSettings: Omit<LabelSettingsV1, 'version'>;
  playbackSettings: Omit<PlaybackSettingsV1, 'version'>;
  PK: string;
};

export const USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME = 'thumbnailCamera';
export const DEFAULT_CHARACTER_ID = 'char-upload-Axel';

const params = new URLSearchParams(window.location.search);
const defaultFrameRate = params.get(
  USE_DEFAULT_THUMBNAIL_CAMERA_QUERY_PARAM_NAME,
)
  ? 24
  : 60;

// added a Hack to give Levi an override for default hex color and gradient
const defaultHexColor = localStorage.getItem('defaultHexColor') ?? '#42aad7';
const defaultUseBackgroundGradient =
  localStorage.getItem('defaultUseBackgroundGradient') ?? 'true';

export const defaultValues: SceneSettingsState = {
  backgroundSettings: {
    enableShadows: true,
    hexColor: defaultHexColor,
    canvasHeight: undefined,
    canvasWidth: undefined,
    gridCellSize: 1,
    showGrid: false,
    showGroundPlane: false,
    useBackgroundGradient: defaultUseBackgroundGradient === 'true',
  },
  cameraSettings: {
    aspectRatio: -1,
    cameraFollow: false,
    serializedCamera: '',
  },
  characterSettings: {
    charUploadID: DEFAULT_CHARACTER_ID,
    rootPosition: [0, 0.5, 0],
    rootRotation: [0, 0, 0],
    faceExpression: FACE_EXPRESSIONS.DEFAULT,
    skinHex: 'default',
    staticHands: HAND_OPTIONS.DEFAULT,
    showSkeleton: false,
    enableRootMotion: true,
  },
  labelSettings: {
    text: '',
    size: 8,
    marginTop: 5,
    marginLeft: 5,
    marginRight: 5,
    color: '#000000',
    style: 'style-1',
    alignment: 'center',
    layer: 'back',
  },
  playbackSettings: {
    frameRate: defaultFrameRate,
    frameStepSize: 1,
    playbackSpeed: 1,
  },
  PK: '',
};

export const state = proxy<SceneSettingsState>(defaultValues);

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'SceneSettings', enabled: true });
}

export function set<
  G extends keyof SceneSettingsState,
  K extends keyof SceneSettingsState[G],
>(group: G, key: K, value: SceneSettingsState[G][K]): void {
  if (!state[group]) return;
  if (state[group][key] === value) return;

  state[group][key] = value;
}

let unsubscribeAutosave: () => void;

export function resetCharacter() {
  state.characterSettings.charUploadID = DEFAULT_CHARACTER_ID;
  state.characterSettings.skinHex = 'default';

  // Also update the character in the characterPickerStore
  characterPickerStore.setActiveCharacterID(DEFAULT_CHARACTER_ID);
}

export function save() {
  debouncedSaveFN();
}

export function reset(resetState?: SceneSettings) {
  unsubscribeAutosave?.();

  const values = transformApiShape(resetState) || defaultValues;

  setStoreValues(state, values);

  unsubscribeAutosave = subscribe(state, debouncedSaveFN);
}

const debouncedSaveFN = debounce(saveSceneSettings, 1000);

async function saveSceneSettings() {
  // ----- Motion side (jobs) -----
  const currentUser = authStore.state.userID;
  const jobID =
    animationGenerationStore.state.jobData?.jobID ||
    projectStore.state.currentProject?.data?.projectID;
  const createdBy =
    animationGenerationStore.state.jobData?.createdBy ||
    projectStore.state.currentProject?.data?.createdBy;

  if (jobID) {
    if (createdBy !== currentUser) {
      console.warn(
        'Not saving settings because current user did not create the job',
      );
      return;
    }
    const sceneSettingsOutput = await postScene(
      jobID,
      transformStateToApiShape(),
    );
    if (sceneSettingsOutput.error) {
      console.error(
        'Failed to save scene settings:',
        sceneSettingsOutput.error,
      );
      return;
    }
    if (sceneSettingsOutput.data) {
      state.PK = sceneSettingsOutput.data.PK;
    }
    return;
  }
}

export async function updateCharacter(characterID: string) {
  state.characterSettings.charUploadID = characterID;
  state.characterSettings.skinHex = defaultSkinTone(undefined, characterID);
}

export function setAspectRatio(aspectRatio: number) {
  state.cameraSettings.aspectRatio = aspectRatio;
}

// Define the old and new face expression enums
enum OLD_FACE_EXPRESSIONS {
  NEUTRAL = 'neutral',
  HAPPY = 'happy',
  SAD = 'sad',
  ANGRY = 'angry',
  SURPRISED = 'surprised',
  HURT = 'hurt',
  THINKING = 'thinking',
  CONFUSED = 'confused',
  RELIEVED = 'relieved',
  BORED = 'bored',
}

// Map old face expressions to new ones
export const mapOldFaceToNew = (oldFace: string): string => {
  // Handle lowercase and old enum values
  if (!oldFace) {
    return FACE_EXPRESSIONS.DEFAULT;
  }
  const normalizedFace = oldFace.toLowerCase();

  switch (normalizedFace) {
    case OLD_FACE_EXPRESSIONS.NEUTRAL:
      return FACE_EXPRESSIONS.NEUTRAL;
    case OLD_FACE_EXPRESSIONS.HAPPY:
      return FACE_EXPRESSIONS.DELIGHTED;
    case OLD_FACE_EXPRESSIONS.SAD:
      return FACE_EXPRESSIONS.SAD;
    case OLD_FACE_EXPRESSIONS.ANGRY:
      return FACE_EXPRESSIONS.ANGRY;
    case OLD_FACE_EXPRESSIONS.SURPRISED:
      return FACE_EXPRESSIONS.SURPRISE;
    case OLD_FACE_EXPRESSIONS.HURT:
      return FACE_EXPRESSIONS.PAIN;
    case OLD_FACE_EXPRESSIONS.THINKING:
      return FACE_EXPRESSIONS.THINKING_LOOK_RIGHT;
    case OLD_FACE_EXPRESSIONS.CONFUSED:
      return FACE_EXPRESSIONS.CONFUSION;
    case OLD_FACE_EXPRESSIONS.RELIEVED:
      return FACE_EXPRESSIONS.PLEASED;
    case OLD_FACE_EXPRESSIONS.BORED:
      return FACE_EXPRESSIONS.BORED;
    default:
      // If we encounter an unknown old expression or already new expression,
      // check if it exists in the new enum, otherwise default to NEUTRAL
      return Object.values(FACE_EXPRESSIONS).includes(
        oldFace as FACE_EXPRESSIONS,
      )
        ? oldFace
        : FACE_EXPRESSIONS.NEUTRAL;
  }
};

export function transformStateToApiShape() {
  return {
    backgroundSettings: {
      ...state.backgroundSettings,
      version: '1.0.0' as const,
    },
    cameraSettings: {
      ...state.cameraSettings,
      version: '1.0.0' as const,
    },
    playbackSettings: {
      ...state.playbackSettings,
      version: '1.0.0' as const,
    },
    characterSettings: {
      ...state.characterSettings,
      version: '1.0.0' as const,
    },
    labelSettings: {
      ...state.labelSettings,
      version: '1.0.0' as const,
    },
  };
}

function transformApiShape(
  object?: SceneSettings,
): SceneSettingsState | undefined {
  if (!object) return;
  let hasFace = object?.characterSettings?.faceExpression;
  if (hasFace) {
    hasFace = mapOldFaceToNew(hasFace);
  } else {
    hasFace = FACE_EXPRESSIONS.DEFAULT;
  }

  return {
    backgroundSettings: {
      ...defaultValues.backgroundSettings,
      ...object.backgroundSettings,
    },
    cameraSettings: {
      ...defaultValues.cameraSettings,
      ...object.cameraSettings,
    },
    characterSettings: {
      ...defaultValues.characterSettings,
      ...object.characterSettings,
      faceExpression: hasFace,
    },
    labelSettings: {
      ...defaultValues.labelSettings,
      ...object.labelSettings,
    },
    playbackSettings: {
      ...defaultValues.playbackSettings,
      ...object.playbackSettings,
    },
    PK: object.PK,
  };
}
